/**
 * DepthOverlay - Depth Map Overlay on Video
 *
 * Renders a color-coded depth heatmap over the video player.
 * Semi-transparent overlay with blue (close) to red (far) gradient.
 */

import React, { useRef, useEffect } from 'react';

/**
 * DepthOverlay Component
 *
 * @param {Object} props
 * @param {string} props.depthData - Base64 encoded depth map image
 * @param {number} props.width - Canvas width (should match video width)
 * @param {number} props.height - Canvas height (should match video height)
 * @param {number} props.opacity - Overlay opacity (0-1)
 * @param {boolean} props.visible - Whether overlay is visible
 */
const DepthOverlay = ({
  depthData,
  width = 640,
  height = 360,
  opacity = 0.5,
  visible = true
}) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!depthData || !visible || !canvasRef.current) {
      // Clear canvas if not visible
      if (canvasRef.current) {
        const ctx = canvasRef.current.getContext('2d');
        ctx.clearRect(0, 0, width, height);
      }
      return;
    }

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    // Load depth image
    const img = new Image();
    img.onload = () => {
      // Draw depth map
      ctx.clearRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0, width, height);

      // Apply color mapping (grayscale depth -> blue-red gradient)
      const imageData = ctx.getImageData(0, 0, width, height);
      const data = imageData.data;

      for (let i = 0; i < data.length; i += 4) {
        const depth = data[i]; // Use red channel as depth value (0-255)
        const normalized = depth / 255;

        // Blue (close) to Red (far) gradient
        const r = Math.floor(normalized * 255);
        const g = Math.floor((1 - Math.abs(normalized - 0.5) * 2) * 255);
        const b = Math.floor((1 - normalized) * 255);

        data[i] = r;
        data[i + 1] = g;
        data[i + 2] = b;
        data[i + 3] = Math.floor(opacity * 255); // Semi-transparent
      }

      ctx.putImageData(imageData, 0, 0);
    };

    // Handle base64 or URL
    if (depthData.startsWith('data:')) {
      img.src = depthData;
    } else {
      img.src = `data:image/png;base64,${depthData}`;
    }
  }, [depthData, width, height, opacity, visible]);

  if (!visible) return null;

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      className="absolute inset-0 pointer-events-none"
      style={{
        mixBlendMode: 'screen',
        opacity: opacity,
      }}
    />
  );
};

export default DepthOverlay;
